// Class
import _ from "lodash";
import { DateUtils } from "../utils/DateUtils";
import { Contact } from "./Contact";
import clip from "text-clipper";
import { Intervention } from "./Intervention";
import moment from "moment";
import { Employe } from "./Employe";
import { Email } from "./Email";
import StringUtils from "../utils/StringUtils";

export class JournalEntry {
    id?: number;
    parent_id?: number;
    reference_id?: number;
    description: string = "";
    creator_name: string = "";
    _recipient?: any;
    recipient_id?: number;
    recipient_type?: JournalEntry.ERecipientType;
    recipient_name: string = "";
    internal_recipient_id?: number;
    _client?: Contact;
    client_id?: number;
    _object: any;
    object_id?: number;
    _employe?: Employe;
    employe_id?: number;
    comment_object?: JournalEntry.EObject;
    status: JournalEntry.EStatus = JournalEntry.EStatus.Pending;
    category: JournalEntry.ECategory = JournalEntry.ECategory.General;
    communication_type?: JournalEntry.ECommunicationType;
    files: Array<any> = [];
    filesIds: Array<number> = [];
    photoIds: Array<number> = [];
    comments: Array<JournalEntry> = [];
    attachment: any;
    attachment_type?: JournalEntry.EAttachmentType;
    attachment_id?: number;
    request_id?: number;
    date_echeance?: string;
    mails: Array<Email> = [];
    created_at?: string;
    updated_at?: string;
    old_format_folder = false;

    constructor(data: any) {
        _.assign(
            this,
            _.omit(data, [
                "client",
                "comments",
                "recipient",
                "object",
                "employe",
            ])
        );
        if (data.client) {
            this._client = new Contact(data.client);
        }
        if (data.comments) {
            this.comments = _.map(data.comments, function (c) {
                return new JournalEntry(c);
            });
        }
        if (data.recipient) {
            if (data.recipient_type === JournalEntry.ERecipientType.Employe) {
                this._recipient = new Employe(data.recipient);
            }
        }
        if (data.object) {
            this._object = data.object;
        }
        if (data.employe) {
            this.employe = new Employe(data.employe);
        }
        // Fix for old jounal entries
        if (!this.parent_id && this.attachment) {
            let duplicata = _.clone(this);
            duplicata.comments = [];
            this.comments.push(duplicata);
            this.old_format_folder = true;
        }
    }

    //
    get client(): Contact | undefined {
        return this._client;
    }

    set client(value: Contact | undefined) {
        if (value) {
            this._client = value;
            this.client_id = value.id;
        }
    }

    //
    get recipient(): Employe | undefined {
        return this._recipient;
    }

    set recipient(value: Employe | undefined) {
        if (value) {
            this._recipient = value;
            this.recipient_id = value.id;
            this.recipient_type = JournalEntry.ERecipientType.Employe;
        }
    }

    //
    get employe(): Employe | undefined {
        return this._employe;
    }

    set employe(value: Employe | undefined) {
        if (value) {
            this._employe = value;
            this.employe_id = value.id;
        } else {
            this._employe = undefined;
            this.employe_id = undefined;
        }
    }

    //
    get object(): any | undefined {
        return this._object;
    }

    set object(value: any | undefined) {
        if (value) {
            this._object = value;
            this.object_id = value.id;
        }
    }

    get short_desc(): string {
        return clip(this.description, 150, { html: true, maxLines: 2 });
    }

    get creation_date_str(): string {
        if (this.created_at) {
            return DateUtils.sqlToDateStr(this.created_at);
        } else {
            return "";
        }
    }

    get creation_date_hour_str(): string {
        if (this.created_at) {
            return DateUtils.sqlToDateStr(this.created_at, "DD/MM/YYYY HH:mm");
        } else {
            return "";
        }
    }

    get updated_date_str(): string {
        if (this.updated_at) {
            return DateUtils.sqlToDateStr(this.updated_at);
        } else {
            return "";
        }
    }

    get updated_date_hour_str(): string {
        if (this.updated_at) {
            return DateUtils.sqlToDateStr(this.updated_at, "DD/MM/YYYY HH:mm");
        } else {
            return "";
        }
    }
    get date_echeance_str(): string {
        if (this.date_echeance) {
            return DateUtils.sqlToDateStr(this.date_echeance, "DD/MM/YYYY");
        } else {
            return "";
        }
    }
    set date_echeance_str(date: string) {
        var newDate = DateUtils.dateStrToSql(date);

        if (!_.isEmpty(newDate)) {
            this.date_echeance = newDate;
        }
    }

    get isNew(): boolean {
        if (
            this.status === JournalEntry.EStatus.Pending &&
            !this.date_echeance
        ) {
            if (this.comments.length === 0) {
                return true;
            } else if (
                this.object &&
                this.object.name ===
                    JournalEntry.ObjectName[
                        JournalEntry.EObject.incoming_mail
                    ] &&
                this.comments.length <= 1
            ) {
                return true;
            }
        }
        return false;
    }

    get isLate(): boolean {
        if (!(this.status === JournalEntry.EStatus.Pending)) {
            return false;
        }
        let now = moment();
        if (this.date_echeance) {
            return this.date_echeance < now.format("YYYY-MM-DD");
        } else {
            return false;
        }
    }

    get interventions(): Array<Intervention> {
        let interventions = _.map(this.comments, function (c) {
            if (
                c.attachment_type ===
                    JournalEntry.EAttachmentType.Intervention &&
                c.attachment
            ) {
                return new Intervention(c.attachment);
            }
        });
        return _.compact(interventions);
    }

    get finished_interventions(): Array<Intervention> {
        return _.filter(
            this.interventions,
            function (intervention: Intervention) {
                return (
                    intervention.etat === Intervention.EEtat.Finished ||
                    intervention.etat === Intervention.EEtat.Archived
                );
            }
        );
    }

    get pending_interventions(): Array<Intervention> {
        return _.filter(this.interventions, function (i) {
            return (
                i.etat === Intervention.EEtat.Pending ||
                i.etat === Intervention.EEtat.Ongoing
            );
        });
    }

    get closed_comments(): Array<JournalEntry> {
        return _.filter(this.comments, { status: JournalEntry.EStatus.Closed });
    }

    get pending_comments(): Array<JournalEntry> {
        return _.filter(this.comments, {
            status: JournalEntry.EStatus.Pending,
        });
    }

    get status_css_class(): string {
        if (this.status === JournalEntry.EStatus.Closed) {
            return "status-closed";
        } else if (this.isNew) {
            return "status-new";
        } else if (this.isLate) {
            return "status-late";
        } else if (this.status === JournalEntry.EStatus.Pending) {
            return "status-pending";
        }
        return "";
    }

    get sanitizedDescription(): string {
        return StringUtils.stripHtmlStyle(this.description);
    }

    get condensedDescription(): string {
        return StringUtils.condensateHtml(this.sanitizedDescription);
    }

    get shortCondensedDescription(): string {
        return clip(this.condensedDescription, 1500, {
            html: true,
            maxLines: 5,
        });
    }

    get_comments_attachments(withMailAttachments?: boolean): Array<any> {
        return _.reduce(
            this.comments,
            function (res, comment: JournalEntry) {
                return _.concat(
                    res,
                    comment.files,
                    withMailAttachments ? comment.mail_attachments : []
                );
            },
            new Array<any>()
        );
    }

    get mail_attachments(): Array<any> {
        if (
            this.attachment_type === JournalEntry.EAttachmentType.Email &&
            this.attachment
        ) {
            return this.attachment.attachments;
        } else if (
            this.attachment_type === JournalEntry.EAttachmentType.Intervention
        ) {
            return _.chain(this.mails).map("attachments").flatten().value();
        } else {
            return [];
        }
    }

    // Mails affichables dans la modale d'historique : ceux de l'entrée, ou les
    // notifications d'une absence ramenées au même format
    get displayableMails(): Array<any> {
        if (
            this.attachment_type === JournalEntry.EAttachmentType.Absence &&
            this.attachment
        ) {
            return _.map(
                this.attachment.notifications,
                function (notification: any) {
                    return {
                        type_id: Email.EEmailTypes.outgoing,
                        recipient: notification.recipients,
                        content: notification.content,
                        created_at: notification.created_at,
                    };
                }
            );
        }
        return this.mails;
    }

    // Utils
    get link(): String {
        return "#/app/journal/" + (this.parent_id ? this.parent_id : this.id);
    }

    serialize(): JournalEntry.IJournalEntry {
        let res = {
            id: this.id,
            parent_id: this.parent_id,
            description: this.description,
            recipient_id: this.recipient_id,
            recipient_type: this.recipient_type,
            internal_recipient_id: this.internal_recipient_id,
            client_id: this.client_id,
            object_id: this.object_id,
            employe_id: this.employe_id,
            comment_object: this.comment_object,
            status: this.status,
            category: this.category,
            communication_type: this.communication_type,
            filesIds: this.filesIds,
            photoIds: this.photoIds,
            attachment_type: this.attachment_type,
            attachment_id: this.attachment_id,
            request_id: this.request_id,
            date_echeance: this.date_echeance,
        };

        return res;
    }

    static createFromEmailData(emailData: any, parentId: Number): JournalEntry {
        let body =
            "À : <i>" +
            emailData.to +
            "</i><br />" +
            "<strong>" +
            emailData.subject +
            "</strong><br /><div>" +
            emailData.body +
            "</div>";
        return new JournalEntry({
            recipient_id: emailData.recipient ? emailData.recipient.id : null,
            recipient_type: JournalEntry.ERecipientType.Contact,
            description: body,
            status: JournalEntry.EStatus.Closed,
            parent_id: parentId,
            communication_type: JournalEntry.ECommunicationType.outgoing_email,
            filesIds: emailData.attachments,
            photoIds: emailData.photos,
        });
    }
}

// Enums
export namespace JournalEntry {
    // Interface
    export interface IJournalEntry {
        id?: number;
        parent_id?: number;
        description: string;
        recipient_id?: number;
        recipient_type?: JournalEntry.ERecipientType;
        internal_recipient_id?: number;
        client_id?: number;
        object_id?: number;
        employe_id?: number;
        comment_object?: JournalEntry.EObject;
        status: JournalEntry.EStatus;
        category: JournalEntry.ECategory;
        communication_type?: JournalEntry.ECommunicationType;
        filesIds: Array<number>;
        photoIds: Array<number>;
        attachment_type?: JournalEntry.EAttachmentType;
        attachment_id?: number;
        request_id?: number;
        date_echeance?: string;
    }

    // Type
    export enum EStatus {
        Pending = 0,
        Closed = 1,
        New = -1,
        Late = -2,
    }

    export enum ECategory {
        General = 0,
        Relance = 1,
    }

    // Libellés des catégories de journal : unique point de renommage côté
    // front (cf. JournalEntry::CATEGORY_LABEL côté PHP).
    export const CategoryName: { [index: number]: string } = {
        [JournalEntry.ECategory.General]: "Général",
        [JournalEntry.ECategory.Relance]: "Relances",
    };

    // Valeur "all" : convention de l'API (EntriesQuery) pour "tous les
    // journaux" ; absence de paramètre = journal général seul.
    export const CategoryFilters: Array<{
        value: JournalEntry.ECategory | "all";
        label: string;
    }> = [
        {
            value: JournalEntry.ECategory.General,
            label: CategoryName[JournalEntry.ECategory.General],
        },
        {
            value: JournalEntry.ECategory.Relance,
            label: CategoryName[JournalEntry.ECategory.Relance],
        },
        { value: "all", label: "Tous" },
    ];

    export enum ERecipientType {
        Employe = "App\\Models\\RH\\Employe",
        Contact = "App\\Models\\Client\\Client",
    }

    export enum EAttachmentType {
        Intervention = "App\\Models\\Intervention\\Intervention",
        Email = "App\\Models\\Mail\\Mail",
        Absence = "App\\Models\\RH\\Absence",
        Facturation = "App\\Models\\Intervention\\Facturation",
        Audit = "App\\Models\\Audit\\Evenement",
    }

    export enum EObject {
        incoming_mail = "incoming_mail",
        outgoing_mail = "outgoing_mail",
        incoming_notification = "incoming_notification",
        outgoing_notification = "outgoing_notification",
        intervention_request = "intervention_request",
        intervention_create = "intervention_create",
        intervention_status = "intervention_status",
        facturation = "facturation",
        absence = "absence",
    }

    export const ObjectName: { [index: string]: string } = {
        [JournalEntry.EObject.incoming_mail]: "Email entrant",
        [JournalEntry.EObject.outgoing_mail]: "Email sortant",
        [JournalEntry.EObject.incoming_notification]: "Notification entrante",
        [JournalEntry.EObject.outgoing_notification]: "Notification sortante",
        [JournalEntry.EObject.intervention_request]: "Demande d'intervention",
        [JournalEntry.EObject.intervention_create]: "Création intervention",
        [JournalEntry.EObject.intervention_status]: "Statut intervention",
        [JournalEntry.EObject.facturation]: "Facturation",
        [JournalEntry.EObject.absence]: "Absences",
    };

    export enum ECommunicationType {
        none = 0,
        outgoing_email = 1,
        incoming_email = 2,
        outgoing_direct_message = 3,
        incoming_direct_message = 4,
        intervention_status = 5,
    }

    // UI
    export enum UIActionType {
        send_notification,
        send_email,
        intervention_request,
        create_entry,
        show_entry,
        edit_entry,
        delete_entry,
        move_entry,
        create_absence,
        edit_absence,
        show_intervention,
        create_intervention,
        edit_intervention,
        edit_facturation,
        send_bon_de_passage,
        send_signalement,
        show_evenement,
        show_mails,
    }
}
